import React from "react"
import type { VariantType } from "@shared/graphql.types"
import Image from "next/image"
import { Box, Flex, Text } from "@chakra-ui/react"
import { Rating } from "components/Rating"
import { Link } from "components/Link"
import type { CMSPageProps } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"
import { cmsPageClick } from "packages/acquisition/components/chakra-components/LandingPage/LandingPage.utils"

import { getPDPUrl } from "utils/getPDPUrl"
import { useLocale } from "hooks"
import { trackClick } from "tracking"
import { siteUrl } from "utils/getSiteUrl"
import { useCurrencyName } from "hooks/useCurrencyName"
import { ProductDetailsText } from "./ProductDetailsText"
import { CompareToText, ProductCardContainer, RibbonText } from "./styles"
import { colours } from "@shared/ui.design-system"

type Props = CMSPageProps & {
  variant: VariantType
  ribbon?: string
  compareTo?: string
  hasOneCompareTo?: boolean
}

export const ProductCard: React.FC<Props> = ({
  variant,
  ribbon,
  compareTo,
  trackingModule,
  hasOneCompareTo,
}) => {
  const locale = useLocale()
  const currency = useCurrencyName()

  const {
    review,
    title,
    subTitle,
    brandName,
    detailsMedia,
    sku,
    pricing: { members, typical },
  } = variant

  const image =
    detailsMedia?.thumbnails?.size_460 ??
    detailsMedia?.thumbnails?.size_OTF_BETA

  const clickUrl = getPDPUrl(variant, locale)
  const url = `${siteUrl}${clickUrl}`

  const handleClick = () => {
    trackClick({
      event: "Product Clicked",
      properties: {
        sku,
        name: title,
        members_price: members,
        typical_price: typical,
        url,
        currency,
        brand: brandName,
        variant: subTitle ?? "",
      },
    })
    cmsPageClick({
      ...trackingModule,
      moduleContentUrl: clickUrl,
    })
  }

  return (
    <ProductCardContainer
      display={{ base: "flex" }}
      as={Link}
      href={clickUrl}
      onClick={handleClick}
    >
      <Flex flexDirection="column" gap="1rem">
        {image && (
          <>
            <Box display={{ base: "block", md: "none" }}>
              <Image
                src={image}
                width={400}
                height={400}
                alt={[brandName, title].join(" ")}
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
            <Box display={{ base: "none", md: "block" }}>
              <Image
                src={image}
                width={400}
                height={400}
                alt={[brandName, title].join(" ")}
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          </>
        )}
        {ribbon && <RibbonText variant="acuminLight">{ribbon}</RibbonText>}
        {review && (
          <Box display="flex" pt="0.625rem">
            <Rating
              outOfStock={false}
              reviewCount={review.count}
              reviewStars={review.stars}
              textVariant="acuminRegularM1"
            />
          </Box>
        )}
        {!review && (
          <Box display="flex">
            <ProductDetailsText
              fontWeight={600}
              m={0}
              pt="0.375rem"
              lineHeight="normal"
            >
              <Text as="span" textTransform="uppercase">
                New In
              </Text>
            </ProductDetailsText>
          </Box>
        )}
        <Box w={{ base: "100%", md: "90%" }}>
          <ProductDetailsText variant="acuminMedium" display="block">
            {brandName}{" "}
          </ProductDetailsText>
          <ProductDetailsText variant="acuminLight">
            {title} {subTitle}
          </ProductDetailsText>
        </Box>
      </Flex>
      <Flex flexDirection="column" justifyContent="start">
        {compareTo && (
          <Box pt={{ base: "0.5rem", lg: "1rem" }}>
            <CompareToText color={colours.primary.medium} variant="acuminLight">
              Compare to
            </CompareToText>

            <CompareToText color={colours.primary.dark} variant="acuminLight">
              {compareTo}
            </CompareToText>
          </Box>
        )}
        {!compareTo && hasOneCompareTo && (
          <Box h={{ base: "40px", lg: "48px" }} />
        )}
      </Flex>
    </ProductCardContainer>
  )
}
